export type DbEnv =
  | "develop_local_dump"
  | "stage_local_dump"
  | "prod_local_dump"
  | "local"
  | "local_test"
  | "docker_test"
  | "sandbox1"
  | "sandbox2"
  | "sandbox3"
  | "sandbox4"
  | "sandbox5"
  | "sandbox6"
  | "sandbox7"
  | "sandbox8"
  | "sandbox9"
  | "sandbox10"
  | "sandbox11"
  | "sandbox12"
  | "sandbox13"
  | "sandbox14"
  | "sandbox15"
  | "sandbox16"
  | "sandbox17"
  | "sandbox18"
  | "sandbox19"
  | "sandbox20"
  | "demo1"
  | "demo2"
  | "pr"
  | "develop"
  | "qa"
  | "stage"
  | "prod";

export const remoteDbs: DbEnv[] = [
  "sandbox1",
  "sandbox2",
  "sandbox3",
  "sandbox4",
  "sandbox5",
  "sandbox6",
  "sandbox7",
  "sandbox8",
  "sandbox9",
  "sandbox10",
  "sandbox11",
  "sandbox12",
  "sandbox13",
  "sandbox14",
  "sandbox15",
  "sandbox16",
  "sandbox17",
  "sandbox18",
  "sandbox19",
  "sandbox20",
  "demo1",
  "demo2",
  "pr",
  "develop",
  "qa",
  "stage",
  "prod",
] as const;

export type SandboxDbEnv = Extract<DbEnv, `sandbox${number}`>;

export const sandboxDbs: SandboxDbEnv[] = remoteDbs.filter((db): db is SandboxDbEnv => db.startsWith("sandbox"));

/**
 * Sandboxes reserved as mirrors of the release environments, so the release and hotfix paths can be
 * exercised end to end without touching production. Only these dual-push their build artifacts to the
 * shared store, which is what makes them promotable.
 */
export type ReleaseMirrorSandbox = Extract<DbEnv, "sandbox17" | "sandbox18" | "sandbox19" | "sandbox20">;

export const releaseMirrorSandboxes: ReleaseMirrorSandbox[] = remoteDbs.filter((db): db is ReleaseMirrorSandbox =>
  ["sandbox17", "sandbox18", "sandbox19", "sandbox20"].includes(db),
);

/**
 * Environments a hotfix may target. The release mirrors are included so the path can be rehearsed;
 * the other sandboxes are not, because they deploy from their own branch and a hotfix to one would be
 * meaningless rather than merely harmless.
 */
export type HotfixableEnv = Extract<DbEnv, "demo1" | "demo2" | "stage" | "prod" | "qa"> | ReleaseMirrorSandbox;

export const hotfixableEnvs: HotfixableEnv[] = remoteDbs.filter((db): db is HotfixableEnv =>
  ["demo1", "demo2", "stage", "prod", "qa", ...releaseMirrorSandboxes].includes(db),
);

/**
 * Environments the daily staff-account sync (FDN-1893) provisions and runs
 * against — every remote env except the shared `demo2` and the ephemeral `pr`.
 * Kept in sync with the `ENVS` list in the `sync-staff-accounts-run` CircleCI
 * job by deriving from `remoteDbs`.
 */
export const staffSyncEnvs: DbEnv[] = remoteDbs.filter((db) => db !== "demo2" && db !== "pr");

/**
 * Environments a platform client other than `STAFF_SYNC` may be provisioned in. Wider than
 * {@link staffSyncEnvs} because the `demo2` exclusion is about where the staff sync job runs, not
 * about which environments can hold a client. `pr` stays out: its databases come and go per pull
 * request, so a client seeded there would not outlive it.
 */
export const platformClientEnvs: DbEnv[] = remoteDbs.filter((db) => db !== "pr");
